import type { CSSProperties, ReactNode } from 'react';
import { COULEURS } from '../../app/styles.js';
import { ArbreDessine } from './arbre-dessine.js';
import { DISPOSITIONS_DONNEES, ETATS_BRANCHE } from './libelles.js';
import {
  anneeDeces,
  auteurs,
  conjointsDe,
  enfantsDe,
  generationDe,
  membresDeBranche,
  nomAffiche,
  parentsDe,
  partTransmiseVia,
  personneParId,
  quotePart,
  quotePartBranche,
} from './modele.js';
import { CONTOUR_SELECTION, Noeud, taillePolice, type Affichage, type ContexteNoeud } from './noeud.js';
import type { DonneesArbre, IdDisposition, Personne, StatutContact } from './types.js';

export interface Disposition {
  id: IdDisposition;
  libelle: string;
  aide: string;
}
export const DISPOSITIONS: readonly Disposition[] = DISPOSITIONS_DONNEES;

export interface PropsArbre {
  donnees: DonneesArbre;
  /** `dessine` : le vrai arbre dessiné avec ses liens (D04) ; les cinq autres sont celles de D04b. */
  disposition: IdDisposition | 'dessine';
  /** Présent quand l'arbre est navigable : un seul nœud est atteignable par Tab. */
  focusId?: string;
  heritierId?: string;
  vue?: 'equipe' | 'famille';
  afficher?: Affichage;
  statuts?: Readonly<Record<string, StatutContact>>;
  viewerId?: string;
  /** Équipe : bouton « Ajouter un frère ou une sœur » sur chaque carte de l'arbre dessiné (ADR 0064). */
  onAjouterFratrie?: (id: string) => void;
}

const AFFICHAGE_DEFAUT: Required<Affichage> = { quotesParts: true, defuntsGrises: true };

const rangee: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', margin: '12px 0' };

const personnesDe = (d: DonneesArbre, ids: readonly string[]): Personne[] =>
  ids.flatMap((id) => {
    const p = personneParId(d, id);
    return p ? [p] : [];
  });

function Classique({ ctx }: { ctx: ContexteNoeud }) {
  const d = ctx.donnees;
  const generations = [...new Set(d.personnes.map((p) => generationDe(d, p.id)))].sort((a, b) => a - b);
  return (
    <div data-disposition="classique">
      {generations.map((g) => (
        <div key={g} data-generation={g} role="group" aria-label={`Génération ${g + 1}`} style={rangee}>
          {d.personnes
            .filter((p) => generationDe(d, p.id) === g)
            .map((p) => (
              <Noeud key={p.id} p={p} ctx={ctx} />
            ))}
        </div>
      ))}
    </div>
  );
}

const CENTRE = 320;
const RAYONS = [0, 170, 260, 330, 390];

function texteCourt(p: Personne, ctx: ContexteNoeud): string {
  const annee = anneeDeces(p);
  const quote = ctx.afficher.quotesParts && ctx.vue === 'equipe' ? quotePart(ctx.donnees, p.id) : null;
  return [nomAffiche(p), p.vitalStatus === 'decede' ? `† ${annee ?? 'date inconnue'}` : null, quote].filter(Boolean).join(' · ');
}

function Eventail({ ctx }: { ctx: ContexteNoeud }) {
  const d = ctx.donnees;
  const generations = [...new Set(d.personnes.map((p) => generationDe(d, p.id)))].sort((a, b) => a - b);
  return (
    <svg
      data-disposition="eventail"
      viewBox="0 0 640 640"
      width="100%"
      role="group"
      aria-label="Éventail de la famille"
      style={{ maxWidth: 720 }}
    >
      {generations.map((g) => (
        <circle
          key={g}
          data-cercle={g}
          cx={CENTRE}
          cy={CENTRE}
          r={g === 0 ? 80 : (RAYONS[g] ?? 390)}
          fill="none"
          stroke={COULEURS.bordure}
          strokeDasharray="4 4"
        />
      ))}
      {generations.flatMap((g) => {
        const membres = d.personnes.filter((p) => generationDe(d, p.id) === g);
        return membres.map((p, i) => {
          const angle = g === 0 ? (i === 0 ? Math.PI : 0) : -Math.PI / 2 + (2 * Math.PI * (i + 0.5)) / membres.length;
          const rayon = g === 0 ? 40 : (RAYONS[g] ?? 390);
          const x = Math.round(CENTRE + rayon * Math.cos(angle));
          const y = Math.round(CENTRE + rayon * Math.sin(angle));
          const focus = ctx.focusId;
          return (
            <g
              key={p.id}
              role="treeitem"
              data-noeud={p.id}
              aria-level={g + 1}
              aria-selected={focus === undefined ? undefined : focus === p.id}
              tabIndex={focus === undefined ? undefined : focus === p.id ? 0 : -1}
              data-grise={p.vitalStatus === 'decede' && ctx.afficher.defuntsGrises ? 'true' : undefined}
              opacity={p.vitalStatus === 'decede' && ctx.afficher.defuntsGrises ? 0.7 : 1}
              style={focus === p.id ? { outline: CONTOUR_SELECTION } : undefined}
            >
              {focus === p.id ? (
                <circle data-selection cx={x} cy={y} r={11} fill="none" stroke={COULEURS.marineFonce} strokeWidth={3} />
              ) : null}
              <circle cx={x} cy={y} r={6} fill={p.isHypothesis ? COULEURS.blanc : COULEURS.marine} stroke={COULEURS.marine} />
              <text
                x={x}
                y={y - 12}
                textAnchor="middle"
                fontSize={ctx.vue === 'famille' ? taillePolice('famille') : 13}
                fill={COULEURS.marineFonce}
              >
                {texteCourt(p, ctx)}
              </text>
            </g>
          );
        });
      })}
    </svg>
  );
}

type EtatBranche = keyof typeof ETATS_BRANCHE;

function etatDeBranche(membres: readonly Personne[]): EtatBranche {
  const vivants = membres.filter((p) => p.vitalStatus === 'vivant' && !p.isHypothesis);
  if (vivants.some((p) => p.reachability === 'injoignable')) return 'bloquee';
  if (vivants.some((p) => p.reachability === 'a_relancer') || membres.some((p) => p.isHypothesis)) return 'a_relancer';
  return 'complete';
}

const COULEUR_ETAT: Record<EtatBranche, string> = { complete: COULEURS.indigo, a_relancer: COULEURS.ambre, bloquee: COULEURS.rouge };

function Branches({ ctx }: { ctx: ContexteNoeud }) {
  const d = ctx.donnees;
  const racines = auteurs(d);
  const tetes = [...new Set(racines.flatMap((r) => enfantsDe(d, r)))];
  const tousMembres = new Set(tetes.flatMap((t) => membresDeBranche(d, t)));
  return (
    <div data-disposition="branches">
      <div style={rangee}>
        {personnesDe(d, racines).map((p) => (
          <Noeud key={p.id} p={p} ctx={ctx} />
        ))}
      </div>
      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {tetes.map((tete) => {
          const ids = membresDeBranche(d, tete);
          const membres = personnesDe(d, ids);
          const racine = personneParId(d, tete);
          const prenom = racine?.firstNames.trim().split(/\s+/)[0] ?? '';
          const quote = ctx.vue === 'equipe' && ctx.afficher.quotesParts ? quotePartBranche(d, ids, prenom) : null;
          const etat = etatDeBranche(membres);
          return (
            <section
              key={tete}
              data-branche={tete}
              aria-label={`Branche de ${racine ? nomAffiche(racine) : ''}`}
              style={{ border: `1px solid ${COULEURS.bordure}`, borderRadius: 8, padding: 12 }}
            >
              <h3 style={{ margin: '0 0 8px', fontSize: ctx.vue === 'famille' ? taillePolice('famille') : 16, color: COULEURS.marine }}>
                Branche {racine ? nomAffiche(racine) : ''}
                {quote ? ` · ${quote}` : ''}
              </h3>
              {ctx.vue === 'equipe' ? (
                <p style={{ margin: '0 0 8px', fontSize: taillePolice(ctx.vue), color: COULEUR_ETAT[etat] }}>{ETATS_BRANCHE[etat]}</p>
              ) : null}
              <div style={{ display: 'grid', gap: 8 }}>
                {membres.map((p) => (
                  <Noeud key={p.id} p={p} ctx={ctx} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
      {d.personnes.filter((p) => !racines.includes(p.id) && !tousMembres.has(p.id)).length > 0 ? (
        <div style={rangee}>
          {d.personnes
            .filter((p) => !racines.includes(p.id) && !tousMembres.has(p.id))
            .map((p) => (
              <Noeud key={p.id} p={p} ctx={ctx} />
            ))}
        </div>
      ) : null}
    </div>
  );
}

function ListeImbriquee({ ctx }: { ctx: ContexteNoeud }) {
  const d = ctx.donnees;
  const racines = auteurs(d);
  const tetes = [...new Set(racines.flatMap((r) => enfantsDe(d, r)))];
  const rendus = new Set<string>();
  const noeud = (id: string, enfants: readonly string[]): ReactNode => {
    const p = personneParId(d, id);
    if (!p || rendus.has(id)) return null;
    rendus.add(id);
    const conjoints = conjointsDe(d, id).filter((c) => !racines.includes(c));
    return [
      <Noeud key={id} p={p} ctx={ctx} balise="li">
        {enfants.length > 0 ? (
          <ul role="group" style={{ margin: 0, paddingLeft: 24 }}>
            {enfants.map((e) => noeud(e, enfantsDe(d, e)))}
          </ul>
        ) : null}
      </Noeud>,
      ...conjoints.map((c) => noeud(c, [])),
    ];
  };
  return (
    <div data-disposition="liste">
      <ul role="group" style={{ margin: 0, paddingLeft: 0 }}>
        {racines.map((r, i) => noeud(r, i === 0 ? tetes : []))}
      </ul>
    </div>
  );
}

function ascendants(d: DonneesArbre, id: string): string[][] {
  const niveaux: string[][] = [];
  let courant = [id];
  while (courant.length > 0) {
    niveaux.unshift(courant);
    const parents = [...new Set(courant.flatMap((c) => parentsDe(d, c)))];
    courant = parents.length > 0 ? parents : [];
    if (niveaux.length > 20) break;
  }
  return niveaux;
}

function explicationDePart(d: DonneesArbre, p: Personne): string {
  const nom = nomAffiche(p);
  const part = d.devolution.shares.find((s) => s.personId === p.id);
  if (!part) return `La part de ${nom} n’est pas encore déterminée : elle apparaîtra dès que le dossier sera complété.`;
  const quote = quotePart(d, p.id);
  const via = part.viaPersonId ? personneParId(d, part.viaPersonId) : undefined;
  if (part.mode === 'propre' || !via) return `${nom} reçoit ${quote} directement des auteurs.`;
  const annee = anneeDeces(via);
  const decede = via.sex === 'F' ? 'décédée' : 'décédé';
  const totalVia = partTransmiseVia(d, via.id);
  const cause =
    part.mode === 'transmission'
      ? `${nomAffiche(via)} a hérité de ses parents, puis est ${decede}${annee ? ` en ${annee}` : ''} : sa part${totalVia ? ` (${totalVia})` : ''} passe à ses propres héritiers dans la succession de ${via.firstNames.trim().split(/\s+/)[0]}.`
      : `${nomAffiche(via)} est ${decede}${annee ? ` en ${annee}` : ''} avant les auteurs : ses descendants viennent à sa place (représentation).`;
  return `${cause} ${nom} reçoit ${quote} par ${part.mode === 'transmission' ? 'transmission' : 'représentation'}.`;
}

function Chemin({ ctx, heritierId }: { ctx: ContexteNoeud; heritierId?: string | undefined }) {
  const d = ctx.donnees;
  const heritier = heritierId ? personneParId(d, heritierId) : undefined;
  if (!heritier) {
    return (
      <div data-disposition="chemin">
        <p>Choisissez un héritier pour afficher la lignée qui le relie aux auteurs, avec l’explication de sa part.</p>
      </div>
    );
  }
  const niveaux = ascendants(d, heritier.id);
  return (
    <div data-disposition="chemin">
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {niveaux.map((ids, i) => (
          <li key={ids.join('-')} data-etape={i} style={{ ...rangee, flexDirection: 'column', alignItems: 'center' }}>
            <div style={rangee}>
              {personnesDe(d, ids).map((p) => (
                <Noeud key={p.id} p={p} ctx={ctx} />
              ))}
            </div>
            {i < niveaux.length - 1 ? <span aria-hidden="true">↓</span> : null}
          </li>
        ))}
      </ol>
      <p data-explication style={{ maxWidth: 640 }}>
        {explicationDePart(d, heritier)}
      </p>
    </div>
  );
}

/** Une même famille, cinq dispositions. Les cinq lisent les mêmes données ; seule la mise en page change. */
export function Arbre(props: PropsArbre) {
  const ctx: ContexteNoeud = {
    donnees: props.donnees,
    focusId: props.focusId,
    vue: props.vue ?? 'equipe',
    afficher: { ...AFFICHAGE_DEFAUT, ...props.afficher },
    statuts: props.statuts,
    viewerId: props.viewerId,
    onAjouterFratrie: props.vue === 'famille' ? undefined : props.onAjouterFratrie,
  };
  const racineRole = props.focusId === undefined ? 'tree' : 'group';
  const contenu = {
    dessine: <ArbreDessine ctx={ctx} />,
    classique: <Classique ctx={ctx} />,
    eventail: <Eventail ctx={ctx} />,
    branches: <Branches ctx={ctx} />,
    liste: <ListeImbriquee ctx={ctx} />,
    chemin: <Chemin ctx={ctx} heritierId={props.heritierId} />,
  }[props.disposition];
  return (
    <div role={racineRole} aria-label="Arbre généalogique" data-vue={ctx.vue} style={{ overflowX: 'auto' }}>
      {contenu}
    </div>
  );
}
